import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { useParams } from "react-router-dom";
import logoMatches from "../../static/Home/logo_matches.webp";

const statusConfig = {
  live: {
    label: "LIVE",
    bg: "rgba(211, 47, 47, 0.12)",
    color: "#d32f2f",
  },
  scheduled: {
    label: "SCHEDULED",
    bg: "rgba(8, 112, 162, 0.10)",
    color: "#0870A2",
  },
  finished: {
    label: "FINISHED",
    bg: "rgba(25, 118, 210, 0.10)",
    color: "#1976d2",
  },
  cancelled: {
    label: "CANCELLED",
    bg: "rgba(97, 97, 97, 0.12)",
    color: "#616161",
  },
};

function getEmbedUrl(match) {
  if (match?.stream_url) {
    return match.stream_url.replace("youtube.com/live/", "www.youtube.com/embed/").replace("?feature=share", "");
  }

  const venue = (match?.venue || "").toUpperCase();

  if (venue === "COURT 1") {
    return "https://www.youtube.com/embed/f8OxCNPhFgA";
  }

  if (venue === "COURT 2") {
    return "https://www.youtube.com/embed/aT_NxHQLZRs";
  }

  return null;
}

function formatDateLabel(date, time) {
  if (!date || !time) return "-";

  const today = new Date();
  const matchDate = new Date(`${date}T12:00:00`);

  const isToday =
    today.getFullYear() === matchDate.getFullYear() &&
    today.getMonth() === matchDate.getMonth() &&
    today.getDate() === matchDate.getDate();

  if (isToday) {
    return `TODAY · ${time}`;
  }

  return `${matchDate.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })} · ${time}`;
}

export default function LiveMatchBroadcastSection() {
  const { match_id } = useParams();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const pollRef = useRef(null);
  const mountedRef = useRef(false);

  const clearPoll = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  };

  const schedulePoll = useCallback((status) => {
    clearPoll();

    const delay = status === "live" ? 15000 : 60000;

    pollRef.current = setTimeout(() => {
      if (mountedRef.current) {
        loadMatch(false);
      }
    }, delay);
  }, []);

  const loadMatch = useCallback(async (showLoader = false) => {
    if (!match_id) return;

    if (showLoader) {
      setLoading(true);
    }

    try {
      const res = await fetch(`/matches/public/${match_id}/`);

      if (!res.ok) {
        throw new Error("Unable to load match");
      }

      const data = await res.json();

      if (!mountedRef.current) return;

      setMatch(data);
      setHasError(false);
      schedulePoll(data?.status);
    } catch {
      if (!mountedRef.current) return;
      setHasError(true);
      schedulePoll("scheduled");
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [match_id, schedulePoll]);

  useEffect(() => {
    mountedRef.current = true;
    loadMatch(true);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        loadMatch(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      mountedRef.current = false;
      clearPoll();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [loadMatch]);

  const statusMeta = useMemo(() => {
    return statusConfig[match?.status] || statusConfig.scheduled;
  }, [match?.status]);

  const embedUrl = useMemo(() => getEmbedUrl(match), [match]);
  const isScheduled = match?.status === "scheduled";
  const isLive = match?.status === "live";

  if (loading) {
    return (
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (hasError || !match) {
    return (
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Alert severity="error" sx={{ borderRadius: "16px" }}>
            We could not load this live match right now.
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 4, md: 6 },
        background:
          "linear-gradient(180deg, rgba(8,112,162,0.06) 0%, rgba(255,255,255,1) 42%)",
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={{ xs: 3, md: 4 }}>
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: { xs: "22px", md: "28px" },
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 2.5, sm: 3, md: 4 },
              boxShadow: "0 18px 55px rgba(15, 23, 42, 0.10)",
              border: "1px solid rgba(8,112,162,0.10)",
            }}
          >
            <Stack spacing={{ xs: 2.2, md: 3 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems="center"
                >
                  <Chip
                    label={statusMeta.label}
                    sx={{
                      height: 34,
                      borderRadius: "999px",
                      px: 0.8,
                      backgroundColor: statusMeta.bg,
                      color: statusMeta.color,
                      fontWeight: 800,
                      fontSize: ".78rem",
                    }}
                  />
                  {isLive && (
                    <Typography
                      sx={{
                        fontSize: ".82rem",
                        fontWeight: 700,
                        color: "#d32f2f",
                        letterSpacing: ".04em",
                      }}
                    >
                      LIVE SCORE UPDATES
                    </Typography>
                  )}
                </Stack>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 0.4, sm: 1.2 }}
                  alignItems="center"
                >
                  <Typography
                    sx={{
                      fontSize: { xs: ".88rem", md: ".95rem" },
                      color: "text.secondary",
                      fontWeight: 700,
                      textAlign: "center",
                    }}
                  >
                    {match.category}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: ".88rem", md: ".95rem" },
                      color: "text.secondary",
                      fontWeight: 700,
                      textAlign: "center",
                    }}
                  >
                    {formatDateLabel(match.date, match.time)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: ".88rem", md: ".95rem" },
                      color: "primary.main",
                      fontWeight: 800,
                      textAlign: "center",
                    }}
                  >
                    {match.venue}
                  </Typography>
                </Stack>
              </Stack>

              <Divider />

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={{ xs: 1.2, sm: 2, md: 3 }}
                sx={{ width: "100%" }}
              >
                <Stack
                  spacing={1.2}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={match.team_local_logo}
                    alt={match.team_local}
                    sx={{
                      width: { xs: 70, sm: 100, md: 124 },
                      height: { xs: 70, sm: 100, md: 124 },
                      objectFit: "contain",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: ".95rem", sm: "1.15rem", md: "1.35rem" },
                      fontWeight: 800,
                      textAlign: "center",
                      lineHeight: 1.15,
                      width: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {match.team_local}
                  </Typography>
                </Stack>

                <Stack
                  spacing={{ xs: 1.2, md: 1.5 }}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    flexShrink: 0,
                    minWidth: { xs: 150, sm: 250, md: 320 },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={{ xs: 0.8, sm: 1.4, md: 1.8 }}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box
                      sx={{
                        minWidth: { xs: 56, sm: 94, md: 112 },
                        height: { xs: 62, sm: 96, md: 112 },
                        px: { xs: 1, sm: 1.5 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: { xs: "18px", md: "22px" },
                        background: "linear-gradient(180deg, #ffffff 0%, #f2f7fb 100%)",
                        border: "1px solid rgba(8,112,162,0.14)",
                        boxShadow: "0 16px 34px rgba(8,112,162,0.10)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: "2.2rem", sm: "3.5rem", md: "4.15rem" },
                          fontWeight: 900,
                          color: "primary.main",
                          lineHeight: 1,
                        }}
                      >
                        {isScheduled ? "-" : (match.score_local ?? 0)}
                      </Typography>
                    </Box>

                    <Box
                      component="img"
                      src={logoMatches}
                      alt="vs"
                      sx={{
                        width: { xs: 28, sm: 38, md: 46 },
                        height: { xs: 28, sm: 38, md: 46 },
                        objectFit: "contain",
                        flexShrink: 0,
                      }}
                    />

                    <Box
                      sx={{
                        minWidth: { xs: 56, sm: 94, md: 112 },
                        height: { xs: 62, sm: 96, md: 112 },
                        px: { xs: 1, sm: 1.5 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: { xs: "18px", md: "22px" },
                        background: "linear-gradient(180deg, #ffffff 0%, #f2f7fb 100%)",
                        border: "1px solid rgba(8,112,162,0.14)",
                        boxShadow: "0 16px 34px rgba(8,112,162,0.10)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: "2.2rem", sm: "3.5rem", md: "4.15rem" },
                          fontWeight: 900,
                          color: "primary.main",
                          lineHeight: 1,
                        }}
                      >
                        {isScheduled ? "-" : (match.score_visitor ?? 0)}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>

                <Stack
                  spacing={1.2}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={match.team_visitor_logo}
                    alt={match.team_visitor}
                    sx={{
                      width: { xs: 70, sm: 100, md: 124 },
                      height: { xs: 70, sm: 100, md: 124 },
                      objectFit: "contain",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: ".95rem", sm: "1.15rem", md: "1.35rem" },
                      fontWeight: 800,
                      textAlign: "center",
                      lineHeight: 1.15,
                      width: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {match.team_visitor}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Box>

          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: { xs: "22px", md: "28px" },
              overflow: "hidden",
              boxShadow: "0 18px 55px rgba(15, 23, 42, 0.10)",
              border: "1px solid rgba(8,112,162,0.10)",
            }}
          >
            {embedUrl ? (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  pt: "56.25%",
                  backgroundColor: "#000",
                }}
              >
                <Box
                  component="iframe"
                  src={embedUrl}
                  title={`${match.venue}: SAAC BASKETBALL QUITO 2026`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ p: 3 }}>
                <Alert severity="info" sx={{ borderRadius: "14px" }}>
                  No live stream is available for this court yet.
                </Alert>
              </Box>
            )}
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}